import 'regenerator-runtime/runtime';
import { call, put, fork, takeEvery, takeLatest, take } from 'redux-saga/effects';
import { eventChannel, END } from 'redux-saga';
import { parseError } from 'logic/helpers';
import api from 'api';
import * as actionCreators from './actionCreators';
import * as actions from './actions';

export const mapStorageError = (parsed) => {
  const { status, message } = parsed.error || {};
  if (status === 404 || /not found on storage/i.test(message || '')) {
    return {
      error: {
        ...parsed.error,
        message: {
          id: 'hostFiles.fileMissing',
          defaultMessage: 'File is no longer available.',
        },
        // Drop status so Errors snackbar renders our translated message
        // instead of its 400/404 "Bad URL" fallback.
        status: undefined,
      },
    };
  }
  return parsed;
};

export function* fetchConfigRequest() {
  try {
    const data = yield call(api.fetchHostFilesConfig);
    yield put(actionCreators.fetchHostFilesConfigSuccess(data));
  } catch (e) {
    yield put(actionCreators.fetchHostFilesConfigFailure(parseError(e)));
  }
}

export function* fetchFilesRequest() {
  try {
    const data = yield call(api.fetchHostFiles);
    yield put(actionCreators.fetchHostFilesSuccess(data));
  } catch (e) {
    yield put(actionCreators.fetchHostFilesFailure(parseError(e)));
  }
}

export function* fetchCountsRequest() {
  try {
    const data = yield call(api.fetchHostFilesCounts);
    yield put(actionCreators.fetchHostFilesCountsSuccess(data));
  } catch (e) {
    yield put(actionCreators.fetchHostFilesCountsFailure(parseError(e)));
  }
}

function createUploadChannel(url, file) {
  return eventChannel((emitter) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        emitter({ progress: Math.round((e.loaded / e.total) * 100) });
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        emitter({ success: true });
      } else {
        emitter({ error: true });
      }
      emitter(END);
    };
    xhr.onerror = () => {
      emitter({ error: true });
      emitter(END);
    };
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
    return () => xhr.abort();
  });
}

export function* uploadSingleFile(file, listingIds, fileIndex, fileCount) {
  yield put(
    actionCreators.uploadHostFileBatchProgress({
      fileIndex,
      fileCount,
      fileProgress: 0,
    }),
  );

  // Initiate upload -> get signed URL
  const initData = yield call(api.initiateHostFileUpload, {
    filename: file.name,
    mime_type: file.type,
    size_bytes: file.size,
    listing_ids: listingIds,
  });

  // PUT to S3 with progress
  const channel = yield call(createUploadChannel, initData.signed_url, file);
  try {
    while (true) {
      const event = yield take(channel);
      if (event.progress !== undefined) {
        yield put(
          actionCreators.uploadHostFileBatchProgress({
            fileIndex,
            fileCount,
            fileProgress: event.progress,
          }),
        );
      }
      // The channel emits END right after error/success, which would terminate
      // the saga before any post-loop code runs. Throw from inside the loop so
      // the finally block closes the channel and the parent catch fires.
      if (event.error) {
        throw new Error('Upload failed');
      }
      if (event.success) {
        break;
      }
    }
  } finally {
    channel.close();
  }

  // Confirm upload
  yield call(api.confirmHostFileUpload, {
    s3_key: initData.s3_key,
    filename: initData.filename,
    mime_type: file.type,
    size_bytes: file.size,
    listing_ids: listingIds,
  });
}

export function* uploadFileRequest(req) {
  try {
    const { files, listing_ids } = req.payload;
    const fileCount = files.length;

    if (fileCount === 0) return;

    for (let i = 0; i < fileCount; i++) {
      yield call(uploadSingleFile, files[i], listing_ids, i, fileCount);
    }

    const code = fileCount > 1 ? 'hostFilesUploaded' : 'hostFileUploaded';
    yield put(actionCreators.uploadHostFileSuccess({ success: { code } }));

    // Re-fetch files and counts
    yield put(actionCreators.fetchHostFilesRequest());
    yield put(actionCreators.fetchHostFilesCountsRequest());
  } catch (e) {
    yield put(actionCreators.uploadHostFileFailure(parseError(e)));
  }
}

export function* renameFileRequest(req) {
  try {
    const { id, display_name } = req.payload;
    yield call(api.renameHostFile, id, { display_name });
    yield put(actionCreators.renameHostFileSuccess({ success: { code: 'hostFileRenamed' } }));
    yield put(actionCreators.fetchHostFilesRequest());
  } catch (e) {
    yield put(actionCreators.renameHostFileFailure(parseError(e)));
  }
}

export function* reassignFileRequest(req) {
  try {
    const { id, listing_ids } = req.payload;
    yield call(api.reassignHostFile, id, { listing_ids });
    yield put(actionCreators.reassignHostFileSuccess({ success: { code: 'hostFileReassigned' } }));
    yield put(actionCreators.fetchHostFilesRequest());
    yield put(actionCreators.fetchHostFilesCountsRequest());
  } catch (e) {
    yield put(actionCreators.reassignHostFileFailure(parseError(e)));
  }
}

export function* deleteFileRequest(req) {
  try {
    const { id } = req.payload;
    yield call(api.deleteHostFile, id);
    yield put(actionCreators.deleteHostFileSuccess({ success: { code: 'hostFileDeleted' } }));
    yield put(actionCreators.fetchHostFilesRequest());
    yield put(actionCreators.fetchHostFilesCountsRequest());
  } catch (e) {
    yield put(actionCreators.deleteHostFileFailure(parseError(e)));
  }
}

export function* viewFileRequest(req) {
  try {
    const { id, url } = req.payload;
    let href = url;
    if (!href) {
      const data = yield call(api.downloadHostFile, id);
      href = data.url;
    }
    const link = document.createElement('a');
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e) {
    yield put(actionCreators.viewHostFileFailure(mapStorageError(parseError(e))));
  }
}

export function* downloadFileRequest(req) {
  try {
    const { id, filename } = req.payload;
    const data = yield call(api.downloadHostFile, id);
    const response = yield call(fetch, data.url);
    if (!response.ok) {
      const err = new Error(`Download failed: ${response.status} ${response.statusText}`);
      err.status = response.status;
      err.statusText = response.statusText;
      throw err;
    }
    const blob = yield call([response, response.blob]);
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (e) {
    yield put(actionCreators.downloadHostFileFailure(mapStorageError(parseError(e))));
  }
}

export default [
  fork(takeEvery, actions.FETCH_HOST_FILES_CONFIG_REQUEST, fetchConfigRequest),
  fork(takeEvery, actions.FETCH_HOST_FILES_REQUEST, fetchFilesRequest),
  fork(takeEvery, actions.FETCH_HOST_FILES_COUNTS_REQUEST, fetchCountsRequest),
  fork(takeLatest, actions.UPLOAD_HOST_FILE_REQUEST, uploadFileRequest),
  fork(takeEvery, actions.RENAME_HOST_FILE_REQUEST, renameFileRequest),
  fork(takeEvery, actions.REASSIGN_HOST_FILE_REQUEST, reassignFileRequest),
  fork(takeEvery, actions.DELETE_HOST_FILE_REQUEST, deleteFileRequest),
  fork(takeEvery, actions.VIEW_HOST_FILE_REQUEST, viewFileRequest),
  fork(takeEvery, actions.DOWNLOAD_HOST_FILE_REQUEST, downloadFileRequest),
];
